import { drawingLineAddPoint } from '@features/drawing/model/actions/drawingLineActions.js';
import {
  drawingMeasure,
  drawingPointAdd,
} from '@features/drawing/model/actions/drawingPointActions.js';
import { galleryRequestImages } from '@features/gallery/model/actions.js';
import { useMessages } from '@features/l10n/l10nInjector.js';
import { useMap } from '@features/map/hooks/useMap.js';
import { mapRefocus } from '@features/map/model/actions.js';
import { OpenInExternalAppDropdownItems } from '@features/openInExternalApp/components/OpenInExternalAppMenuItems.js';
import { useOpenInExternalAppMessages } from '@features/openInExternalApp/translations/useOpenInExternalAppMessages.js';
import {
  routePlannerSetFinish,
  routePlannerSetStart,
} from '@features/routePlanner/model/actions.js';
import { searchSetQuery } from '@features/search/model/actions.js';
import type { Modifier, Obj } from '@popperjs/core';
import type { UseDropdownMenuOptions } from '@restart/ui/DropdownMenu';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { useMenuHandler } from '@shared/hooks/useMenuHandler.js';
import { useScrollClasses } from '@shared/hooks/useScrollClasses.js';
import { LeafletMouseEvent } from 'leaflet';
import {
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  FaCamera,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaInfo,
  FaMapMarkerAlt,
  FaPlay,
  FaRegDotCircle,
  FaRuler,
  FaStop,
} from 'react-icons/fa';
import { MdTimeline } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { setTool } from '../store/actions.js';
import classes from './MapContextMenu.module.css';

const initialState = {
  x: 0,
  y: 0,
  lat: 0,
  lon: 0,
  maxHeight: 100000,
};

// this modifier somehow fixes menu
const fixFlashingModifier: Partial<Modifier<'fixFlashing', Obj>> = {
  name: 'fixFlashing',
  phase: 'afterMain',
  enabled: true,
  effect() {},
};

// center the menu on the clicked point and leave an 8px gap for the arrow "ear"
const offsetModifier: Partial<Modifier<'offset', Obj>> = {
  name: 'offset',
  options: {
    offset: ({ popper }: { popper: { width: number } }) => [
      -popper.width / 2,
      8,
    ],
  },
};

// keep the menu start-aligned so the centering offset stays predictable near
// screen edges (no start<->end flip that would shove the menu off the point)
const flipModifier: Partial<Modifier<'flip', Obj>> = {
  name: 'flip',
  options: { flipVariations: false },
};

// place the ear horizontally so it points at the clicked point; react-bootstrap
// doesn't apply popper's own arrow styles, so we compute the offset from
// popper's rects and write it onto our element ourselves
function createArrowModifier(
  arrowRef: RefObject<HTMLSpanElement | null>,
): Modifier<'fmArrow', Obj> {
  return {
    name: 'fmArrow',
    enabled: true,
    phase: 'write',
    fn({ state }) {
      const arrow = arrowRef.current;

      const offsets = state.modifiersData['popperOffsets'] as
        | { x: number }
        | undefined;

      if (!arrow || !offsets) {
        return;
      }

      const { reference, popper } = state.rects;

      const cx = reference.x + reference.width / 2;
      const size = 16; // ear base length
      const pad = 12; // keep the ear off the rounded corners

      const x = Math.max(pad, Math.min(cx - offsets.x, popper.width - pad));

      arrow.style.left = `${x - size / 2}px`;
    },
  };
}

export function MapContextMenu(): ReactElement {
  const m = useMessages();

  const oeam = useOpenInExternalAppMessages();

  const dispatch = useDispatch();

  const [contextMenu, setContextMenu] = useState(initialState);

  const arrowRef = useRef<HTMLSpanElement>(null);

  // Rebuilt every render so its identity changes: right-clicking elsewhere while
  // the menu stays open fires no show-transition, so popper only re-reads the
  // moved toggle when the config (a fresh arrow-modifier closure) changes,
  // busting react-bootstrap's deep-equal modifier memo.
  const popperConfig: UseDropdownMenuOptions['popperConfig'] = {
    modifiers: [
      fixFlashingModifier,
      offsetModifier,
      flipModifier,
      createArrowModifier(arrowRef),
    ],
  };

  const embedFeatures = useAppSelector((state) => state.main.embedFeatures);

  const toggleRef = useRef<HTMLButtonElement>(null);

  const map = useMap();

  const {
    handleSelect,
    menuShown,
    handleMenuToggle,
    closeMenu,
    submenu,
    extraHandler,
  } = useMenuHandler();

  useEffect(() => {
    if (!map) {
      return;
    }

    function handlecontextMenu(e: LeafletMouseEvent) {
      e.originalEvent.preventDefault();

      closeMenu();

      handleMenuToggle(true);

      setContextMenu({
        x: e.containerPoint.x,
        y: e.containerPoint.y,
        lat: e.latlng.lat,
        lon: e.latlng.lng,
        maxHeight:
          window.innerHeight / 2 +
          Math.abs(e.containerPoint.y - window.innerHeight / 2) -
          10,
      });
    }

    map.addEventListener('contextmenu', handlecontextMenu);

    return () => {
      map.removeEventListener('contextmenu', handlecontextMenu);
    };
  }, [closeMenu, handleMenuToggle, map]);

  useEffect(() => {
    if (menuShown) {
      toggleRef.current?.focus();
    }
  }, [menuShown]);

  const zoom = useAppSelector((state) => state.map.zoom);

  const sc = useScrollClasses('vertical');

  const color = useAppSelector((state) => state.drawingSettings.style.color);

  const markerType = useAppSelector(
    (state) => state.drawingSettings.style.markerType,
  );

  const width = useAppSelector((state) => state.drawingSettings.style.width);

  const linesLength = useAppSelector(
    (state) => state.drawingLines.lines.length,
  );

  const pointsLength = useAppSelector(
    (state) => state.drawingPoints.points.length,
  );

  extraHandler.current = useCallback(
    (eventKey: string | null) => {
      switch (eventKey) {
        case 'center':
          dispatch(
            mapRefocus({
              lat: contextMenu.lat,
              lon: contextMenu.lon,
            }),
          );

          closeMenu();

          break;

        case 'measure':
          dispatch(
            drawingMeasure({
              position: {
                lat: contextMenu.lat,
                lon: contextMenu.lon,
              },
            }),
          );

          closeMenu();

          break;

        case 'details':
          dispatch(
            searchSetQuery({
              query:
                '@' +
                contextMenu.lat.toFixed(6) +
                ',' +
                contextMenu.lon.toFixed(6),
            }),
          );

          closeMenu();

          break;

        case 'photos':
          dispatch(
            galleryRequestImages({
              lat: contextMenu.lat,
              lon: contextMenu.lon,
            }),
          );

          closeMenu();

          break;

        case 'addPoint':
          dispatch(
            drawingPointAdd({
              coords: {
                lat: contextMenu.lat,
                lon: contextMenu.lon,
              },
              color,
              markerType,
              id: pointsLength,
            }),
          );

          dispatch(drawingMeasure({}));

          closeMenu();

          break;

        case 'startLine':
          dispatch(setTool({ tool: 'draw-lines', mode: 'activate' }));

          dispatch(
            drawingLineAddPoint({
              lineProps: {
                type: 'line',
                color,
                width,
              },
              point: {
                id: 0,
                lat: contextMenu.lat,
                lon: contextMenu.lon,
              },
              indexOfLineToSelect: linesLength,
              drawing: true,
            }),
          );

          closeMenu();

          break;

        case 'startRoute':
          dispatch(setTool({ tool: 'route-planner', mode: 'activate' }));

          dispatch(
            routePlannerSetStart({
              lat: contextMenu.lat,
              lon: contextMenu.lon,
            }),
          );

          closeMenu();

          break;

        case 'finishRoute':
          dispatch(setTool({ tool: 'route-planner', mode: 'activate' }));

          dispatch(
            routePlannerSetFinish({
              lat: contextMenu.lat,
              lon: contextMenu.lon,
            }),
          );

          closeMenu();

          break;

        default:
          return false;
      }

      return true;
    },
    [
      closeMenu,
      color,
      markerType,
      contextMenu.lat,
      contextMenu.lon,
      dispatch,
      width,
      linesLength,
      pointsLength,
    ],
  );

  return (
    <Dropdown
      show={menuShown}
      onToggle={handleMenuToggle}
      onSelect={handleSelect}
      autoClose="outside"
    >
      <Dropdown.Toggle
        bsPrefix="fm-dropdown-toggle-nocaret"
        ref={toggleRef}
        style={{
          width: 0,
          height: 0,
          margin: 0,
          padding: 0,
          border: 0,
          position: 'absolute',
          left: contextMenu.x,
          top: contextMenu.y,
          pointerEvents: 'none',
        }}
      />

      <Dropdown.Menu
        className="fm-dropdown-with-scroller"
        popperConfig={popperConfig}
      >
        <span ref={arrowRef} className={classes.arrow} />

        <div
          className="fm-menu-scroller"
          ref={sc}
          style={{ maxHeight: contextMenu.maxHeight }}
        >
          <div />

          {submenu === 'openExternally' ? (
            <>
              <Dropdown.Header>
                <FaExternalLinkAlt /> {oeam?.openInExternal}
              </Dropdown.Header>

              <Dropdown.Item as="button" eventKey="submenu-">
                <FaChevronLeft /> {m?.mainMenu.back} <kbd>Esc</kbd>
              </Dropdown.Item>

              <Dropdown.Divider />

              <OpenInExternalAppDropdownItems
                lat={contextMenu.lat}
                lon={contextMenu.lon}
                zoom={zoom}
                includePoint
                copy={false}
              />
            </>
          ) : (
            <>
              <Dropdown.Item as="button" eventKey="center">
                <FaRegDotCircle /> {m?.mapCtxMenu.centerMap}
              </Dropdown.Item>

              <Dropdown.Item as="button" eventKey="measure">
                <FaRuler /> {m?.mapCtxMenu.measurePosition}
              </Dropdown.Item>

              {(!window.fmEmbedded || embedFeatures.includes('search')) && (
                <Dropdown.Item as="button" eventKey="details">
                  <FaInfo /> {m?.mapCtxMenu.queryFeatures}
                </Dropdown.Item>
              )}

              {(!window.fmEmbedded ||
                !embedFeatures.includes('noMapSwitch')) && (
                <Dropdown.Item as="button" eventKey="photos">
                  <FaCamera /> {m?.mapCtxMenu.showPhotos}
                </Dropdown.Item>
              )}

              {!window.fmEmbedded && (
                <>
                  <Dropdown.Item as="button" eventKey="submenu-openExternally">
                    <FaExternalLinkAlt /> {oeam?.openInExternal}{' '}
                    <FaChevronRight />
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item as="button" eventKey="addPoint">
                    <FaMapMarkerAlt /> {m?.mapCtxMenu.addPoint}
                  </Dropdown.Item>

                  <Dropdown.Item as="button" eventKey="startLine">
                    <MdTimeline /> {m?.mapCtxMenu.startLine}
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item as="button" eventKey="startRoute">
                    <FaPlay color="#409a40" /> {m?.mapCtxMenu.startRoute}
                  </Dropdown.Item>

                  <Dropdown.Item as="button" eventKey="finishRoute">
                    <FaStop color="#d9534f" /> {m?.mapCtxMenu.finishRoute}
                  </Dropdown.Item>
                </>
              )}
            </>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
